import {ReactElement, useRef} from 'react';
import {Image} from '@shopify/hydrogen';
import {Swiper, SwiperSlide} from '../../swiper';
import SwiperCore, {Navigation, Autoplay} from 'swiper';
import classNames from 'classnames';
import {WhiskyBarImageType, WhiskyBarType} from '../../types';

SwiperCore.use([Navigation, Autoplay]);

interface BarCarouselProps {
  whiskyBar: WhiskyBarType;
}

export default function BarCarousel({
  whiskyBar,
}: BarCarouselProps): ReactElement {
  const prevNavRef = useRef(null);
  const nextNavRef = useRef(null);
  const hasMultipleSlides = whiskyBar.images.length > 1;

  return (
    <section>
      <div className="hero-slider-outer hero-slider-only-image">
        <Swiper
          id="hero-slider"
          className="hero-slider swiper-wrapper"
          spaceBetween={0}
          slidesPerView={1}
          loop
          autoplay={
            hasMultipleSlides && {
              delay: 6000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          }
          allowTouchMove={hasMultipleSlides}
          navigation={{
            prevEl: prevNavRef.current,
            nextEl: nextNavRef.current,
          }}
          onBeforeInit={({params: {navigation}}: SwiperCore) => {
            if (navigation && typeof navigation !== 'boolean') {
              navigation.prevEl = prevNavRef.current;
              navigation.nextEl = nextNavRef.current;
            }
          }}
        >
          {whiskyBar.images.map((whiskyImage) => (
            <SwiperSlide key={whiskyImage.uuid}>
              <Banner whiskyImage={whiskyImage} />
            </SwiperSlide>
          ))}
          <div
            className={classNames('hero-slider__controls container', {
              hidden: !hasMultipleSlides,
            })}
          >
            <div
              className="hero-slider__control carousel-control-left"
              ref={prevNavRef}
              id="prev-hero-slide"
            >
              <span className="icon-arrow-left" />
            </div>
            <div
              className="hero-slider__control carousel-control-right"
              ref={nextNavRef}
              id="next-hero-slide"
            >
              <span className="icon-arrow-right" />
            </div>
          </div>
        </Swiper>
      </div>
    </section>
  );
}

interface BannerProps {
  whiskyImage: WhiskyBarImageType;
}

function Banner({whiskyImage}: BannerProps) {
  return (
    <div className="hero-slide swiper-slide">
      <div className="hero-slide-d">
        <picture>
          {whiskyImage.image_desktop_webp && (
            <source srcSet={whiskyImage.image_desktop_webp} type="image/webp" />
          )}
          <source srcSet={whiskyImage.image_desktop_jpeg} type="image/jpeg" />
          <Image
            src={whiskyImage.image_desktop_jpeg}
            width={1400}
            height={500}
            alt="Bar"
          />
        </picture>
      </div>
      <div className="hero-slide-m">
        <picture>
          {whiskyImage.image_mobile_webp && (
            <source srcSet={whiskyImage.image_mobile_webp} type="image/webp" />
          )}
          <source srcSet={whiskyImage.image_mobile_jpeg} type="image/jpeg" />
          <Image
            src={whiskyImage.image_mobile_jpeg}
            width={500}
            height={300}
            alt="Bar"
          />
        </picture>
      </div>
    </div>
  );
}
